import { IFacilityDischargeData } from '../types/discharge';
import { transformDischargeData } from './transformDischargeData';
import {
  FacilityRecord,
  OutfallLocationMap,
  getCachedSourceId,
  setCachedSourceId,
  getCachedEffluent,
  setCachedEffluent,
  getCachedOutfallLocations,
  setCachedOutfallLocations,
} from './echoCache';

const TIMEOUT_MS = 60000;
const ECHO_API = `${process.env.REACT_APP_CERES_URL}/api/echo`;

async function fetchWithTimeout(url: string): Promise<any> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const r = await fetch(url, { signal: ctrl.signal });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  } catch (e: any) {
    if (e.name === 'AbortError') throw new Error('Request timed out');
    throw e;
  } finally {
    clearTimeout(t);
  }
}

export async function fetchEffluentChartData(
  frsId: string,
  lat: number,
  lon: number
): Promise<IFacilityDischargeData[]> {
  let facilities: FacilityRecord[];

  // Check source-id cache — skips Calls 1 + 2 on repeat visits
  const cachedFacilities = await getCachedSourceId(frsId);
  if (cachedFacilities && cachedFacilities.length > 0) {
    facilities = cachedFacilities;
  } else {
    // Call 1: resolve NPDES SourceIDs from CWA facility endpoint (all matches by FRS ID)
    const cwaData = await fetchWithTimeout(
      `${ECHO_API}/cwa-facility?frsId=${encodeURIComponent(frsId)}`
    );
    const cwaFacilities: any[] = cwaData?.Results?.Facilities;
    if (!Array.isArray(cwaFacilities)) throw new Error('Unexpected CWA facility payload');

    // Collect all CWA facilities that have a SourceID
    facilities = cwaFacilities
      .filter((f: any) => f?.SourceID)
      .map((f: any) => {
        const facLat = f.FacLat != null ? parseFloat(f.FacLat) : NaN;
        const facLon = f.FacLong != null ? parseFloat(f.FacLong) : NaN;
        return {
          sourceId: f.SourceID as string,
          receivingWater: f.FacDerivedWBDName || '[Receiving water unavailable]',
          huc12: f.FacDerivedWBD ?? '[HUC-12 unavailable]',
          facilityName: f.CWPName ?? '',
          lat: Number.isFinite(facLat) ? facLat : null,
          lon: Number.isFinite(facLon) ? facLon : null,
        };
      });

    if (facilities.length === 0) {
      throw new Error(`No CWA permits found for FRS ID ${frsId}`);
    }

    await setCachedSourceId(frsId, facilities);
  }

  const totalFacilities = facilities.length;

  // Fetch all effluent charts in parallel, using cache when available
  const results = await Promise.all(
    facilities.map(async (facility, facilityIndex) => {
      const cached = await getCachedEffluent(facility.sourceId);
      const effluentData =
        cached ??
        (await (async () => {
          const fresh = await fetchWithTimeout(
            `${ECHO_API}/effluent-chart?id=${encodeURIComponent(facility.sourceId)}`
          );
          await setCachedEffluent(facility.sourceId, fresh);
          return fresh;
        })());

      const permFeatures = effluentData?.Results?.PermFeatures;
      const outfallLocations =
        Array.isArray(permFeatures) && permFeatures.length > 0
          ? await fetchOutfallLocations(facility.sourceId)
          : {};

      return transformDischargeData(
        effluentData,
        facility.lat ?? lat,
        facility.lon ?? lon,
        facilityIndex,
        totalFacilities,
        facility.receivingWater,
        facility.huc12,
        outfallLocations
      );
    })
  );

  return results;
}

async function fetchOutfallLocations(sourceId: string): Promise<OutfallLocationMap> {
  const cached = await getCachedOutfallLocations(sourceId);
  if (cached) return cached;

  try {
    const data = await fetchWithTimeout(
      `${ECHO_API}/outfalls-location?permitNmbr=${encodeURIComponent(sourceId)}`
    );
    const locations: any[] = Array.isArray(data?.locations) ? data.locations : [];
    const map: OutfallLocationMap = {};
    locations.forEach((loc) => {
      const nmbr: string | undefined = loc?.permFeatureNmbr;
      const lat = loc?.lat;
      const lon = loc?.lon;
      if (nmbr && typeof lat === 'number' && typeof lon === 'number') {
        map[nmbr] = { lat, lon };
      }
    });
    await setCachedOutfallLocations(sourceId, map);
    return map;
  } catch {
    return {};
  }
}
